/**
 * src/components/public/layout/MobileMenuDrawer.tsx
 *
 * Enterprise mobile menu — minimal, breathing, Apple-style.
 *
 * Tasarim prensipleri:
 *  - Tek akis: Logo → Nav → Contact → CTA → Ayarlar (kenarlik gurultusu minimal)
 *  - Buyuk tap target'lar (h-14/h-16) — Apple HIG 44px+, Material 48dp+
 *  - Panel sliding (iOS-style): parent'a tap → yeni panel kayar; back ile geri
 *  - Subtle separator'lar: border-t/border-b yerine bg-slate-100 spacer divs
 *  - Tema/Dil sade tek satir (TR | EN  •  ☾)  — TEMA/DİL labellari yok
 *  - Search bar Phase α'da gosterilmiyor (Phase β functional ile geri gelecek)
 *
 * Light/dark color uyumu:
 *  - Drawer arka plan: bg-white / dark:bg-slate-950
 *  - Item hover: bg-slate-50 / dark:bg-slate-900
 *  - Footer bg yok (bos), border-t ile ayrilir
 *  - Tum text-renkleri her iki tema'da contrast korur
 *
 * Phase β: `contact`, `social`, `topbar` artik parent Server Component'ten prop
 * olarak gelir — bu client component server-only repo cagirabilemez; HeaderMainNav
 * (async Server Component) veriyi DB'den cekip buraya gecer. Hardcoded fallback
 * parent tarafinda zaten uygulanmistir.
 */

'use client'

import { Drawer } from '@heroui/react'
import { ChevronLeft, ChevronRight, Linkedin, Mail, Menu, Moon, Phone, Sun, X } from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'
import { useTheme } from 'next-themes'
import { useEffect, useMemo, useState, useTransition } from 'react'

import type { ContactInfo, NavMenuItem, SiteSettings, SocialLink } from '@/data/types/site-settings'
import { pickLocalized } from '@/domain/value-objects/localized-string'
import type { Locale } from '@/domain/value-objects/locale'
import { Link, usePathname, useRouter } from '@/lib/i18n/navigation'
import { LOCALE_LABELS, SUPPORTED_LOCALES } from '@/lib/i18n/config'

import { Logo } from './Logo'

interface MobileMenuDrawerProps {
  items: NavMenuItem[]
  locale: Locale
  /** Iletisim bilgileri — HeaderMainNav (Server Component) DB'den cekip gonderir. */
  contact: ContactInfo
  /** Sosyal medya linkleri — HeaderMainNav'dan prop olarak gelir. */
  social: SocialLink[]
  /** Top bar config (CTA vs) — footer satiri icin gerekli. */
  topbar: SiteSettings['topbar']
  /** Runtime enabled locale code listesi — switcher filtre için. */
  enabledLocales?: readonly string[]
}

export function MobileMenuDrawer({
  items,
  locale,
  contact,
  social,
  topbar,
  enabledLocales,
}: MobileMenuDrawerProps) {
  const t = useTranslations('a11y')
  const [isOpen, setIsOpen] = useState(false)
  const [path, setPath] = useState<NavMenuItem[]>([])

  function handleOpenChange(open: boolean) {
    setIsOpen(open)
    if (!open) setPath([])
  }

  function drillInto(item: NavMenuItem) {
    if (item.children && item.children.length > 0) {
      setPath((prev) => [...prev, item])
    }
  }

  function goBack() {
    setPath((prev) => prev.slice(0, -1))
  }

  function handleNavigate() {
    setIsOpen(false)
    setPath([])
  }

  /**
   * Topbar'a alinan ikincil linkler (Blog gibi) — ana menude yer kaplamasin diye
   * oraya tasinmislardi, ama HeaderTopBar'da o satir `hidden md:flex` icinde:
   * mobilde HICBIR yerde gorunmuyorlardi. Anasayfadaki "Tum Makaleler" butonu
   * disinda blog'a mobilden ulasilamiyordu (ic sayfalarda tek /blog linki vardi,
   * o da gizli olan topbar linkiydi).
   *
   * Drawer `topbar` prop'unu zaten aliyordu (yalniz `cta`'sini kullaniyordu),
   * bu yuzden ek veri tasima gerekmedi. Ust seviye listeye eklenir; alt
   * panellerde (path.length > 0) tekrarlanmaz.
   *
   * href eslesmesi olanlar elenir — Ramazan ayni hedefi ileride ana menuye de
   * eklerse mobilde iki kez gorunmesin.
   */
  const ikincilLinkler = useMemo<NavMenuItem[]>(() => {
    const mevcutHrefler = new Set(items.map((item) => item.href).filter(Boolean))
    return (topbar.links ?? [])
      .filter((link) => !mevcutHrefler.has(link.href))
      .map((link) => ({ label: link.label, href: link.href }))
  }, [items, topbar.links])

  const currentLevel =
    path.length === 0 ? [...items, ...ikincilLinkler] : (path[path.length - 1]?.children ?? [])
  const currentTitle =
    path.length === 0 ? null : pickLocalized(path[path.length - 1]!.label, locale)
  const currentParentHref = path.length === 0 ? null : path[path.length - 1]!.href

  return (
    <>
      {/* Hamburger trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        aria-label={t('menu_open')}
        className="inline-flex h-10 w-10 items-center justify-center rounded-md text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
      >
        <Menu size={22} aria-hidden="true" />
      </button>

      <Drawer.Backdrop
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        className="bg-slate-900/50 backdrop-blur-sm data-[entering]:duration-300 data-[exiting]:duration-300"
      >
        <Drawer.Content placement="right">
          <Drawer.Dialog className="bg-white dark:bg-slate-950 w-full max-w-sm flex flex-col data-[entering]:duration-300 data-[exiting]:duration-300 data-[entering]:ease-out data-[exiting]:ease-in shadow-2xl">
            <Drawer.Heading className="sr-only">{t('mobile_menu')}</Drawer.Heading>

            {/* Header: Logo + Close */}
            <header className="flex items-center justify-between px-5 h-16 flex-shrink-0">
              <Logo width={120} homeAriaLabel={t('logo_home_link')} />
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                aria-label={t('menu_close')}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200 transition-colors -me-2"
              >
                <X size={20} aria-hidden="true" />
              </button>
            </header>

            {/* Subtle divider */}
            <div className="h-px bg-slate-100 dark:bg-slate-800 mx-5" />

            {/* Breadcrumb (sub-panel) */}
            {path.length > 0 && currentTitle && (
              <BreadcrumbBar
                title={currentTitle}
                onBack={goBack}
                parentHref={currentParentHref}
                onNavigate={handleNavigate}
                locale={locale}
              />
            )}

            {/* Nav list (scrollable) */}
            <Drawer.Body className="flex-1 px-2 py-2 overflow-y-auto">
              <ul
                key={path.length}
                className="flex flex-col animate-in fade-in slide-in-from-right-3 duration-300 ease-out"
              >
                {currentLevel.map((item, idx) => (
                  <NavItem
                    key={`lvl-${path.length}-${idx}-${pickLocalized(item.label, locale)}`}
                    item={item}
                    locale={locale}
                    onDrill={() => drillInto(item)}
                    onNavigate={handleNavigate}
                  />
                ))}
              </ul>
            </Drawer.Body>

            {/* Footer: contact + CTA + settings — data parent'tan prop ile gelir */}
            <DrawerFooter
              contact={contact}
              social={social}
              topbar={topbar}
              {...(enabledLocales ? { enabledLocales } : {})}
            />
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
    </>
  )
}

// ─── Nav item ────────────────────────────────────────────────────────────

function NavItem({
  item,
  locale,
  onDrill,
  onNavigate,
}: {
  item: NavMenuItem
  locale: Locale
  onDrill: () => void
  onNavigate: () => void
}) {
  const router = useRouter()
  const label = pickLocalized(item.label, locale)
  const hasChildren = Boolean(item.children && item.children.length > 0)

  function handleClick() {
    if (hasChildren) {
      onDrill()
    } else if (item.href) {
      onNavigate()
      router.push(item.href)
    }
  }

  return (
    <li>
      <button
        type="button"
        onClick={handleClick}
        className="flex w-full items-center justify-between gap-3 px-3 h-14 rounded-lg text-start text-[15px] font-medium text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors"
      >
        <span className="truncate">{label}</span>
        {hasChildren && (
          <ChevronRight
            size={18}
            aria-hidden="true"
            className="flex-shrink-0 text-slate-400 dark:text-slate-500"
          />
        )}
      </button>
    </li>
  )
}

// ─── Breadcrumb ──────────────────────────────────────────────────────────

function BreadcrumbBar({
  title,
  onBack,
  parentHref,
  onNavigate,
  locale,
}: {
  title: string
  onBack: () => void
  parentHref: string | null
  onNavigate: () => void
  locale: Locale
}) {
  void locale
  const t = useTranslations('common')
  return (
    <div className="px-2 pt-2">
      <button
        type="button"
        onClick={onBack}
        className="flex w-full items-center gap-2 px-3 h-11 rounded-lg text-sm font-medium text-brand-700 dark:text-brand-300 hover:bg-brand-50 dark:hover:bg-slate-900 transition-colors"
      >
        <ChevronLeft size={18} aria-hidden="true" />
        <span className="truncate font-semibold">{title}</span>
      </button>
      {parentHref && (
        <Link
          href={parentHref}
          onClick={onNavigate}
          className="block px-3 mt-1 h-9 inline-flex items-center text-xs uppercase tracking-wider font-semibold text-accent-600 dark:text-accent-500 hover:underline"
        >
          {t('view_all')} →
        </Link>
      )}
    </div>
  )
}

// ─── Footer ──────────────────────────────────────────────────────────────

/**
 * DrawerFooter — mobil menu'nun alt bolumu: iletisim, CTA, locale/tema/sosyal ayarlar.
 *
 * Phase β: veri parent (MobileMenuDrawer) → HeaderMainNav Server Component
 * zincirinden prop olarak gelir. Bu component 'use client' oldugu icin server-only
 * repo'ya erisemez; props ile beslenir.
 *
 * @param contact Iletisim bilgileri (telefon, email)
 * @param social Sosyal medya linkleri
 * @param topbar Top bar config (CTA etiketi + href)
 */
function DrawerFooter({
  contact,
  social,
  topbar,
  enabledLocales,
}: {
  contact: ContactInfo
  social: SocialLink[]
  topbar: SiteSettings['topbar']
  enabledLocales?: readonly string[]
}) {
  const locale = useLocale() as Locale
  const t = useTranslations('a11y')
  const cta = topbar.cta

  return (
    <footer className="flex-shrink-0 border-t border-slate-100 dark:border-slate-800">
      {/* Contact block: phone + email */}
      <div className="px-5 py-4 flex flex-col gap-2 text-sm">
        <a
          href={contact.telefonHref}
          className="inline-flex items-center gap-2.5 text-slate-700 dark:text-slate-200 hover:text-brand-700 dark:hover:text-brand-300 transition-colors"
        >
          <Phone size={15} aria-hidden="true" className="text-slate-400 dark:text-slate-500" />
          <span className="font-medium">{contact.telefon}</span>
        </a>
        <a
          href={`mailto:${contact.email}`}
          className="inline-flex items-center gap-2.5 text-slate-700 dark:text-slate-200 hover:text-brand-700 dark:hover:text-brand-300 transition-colors truncate"
        >
          <Mail size={15} aria-hidden="true" className="text-slate-400 dark:text-slate-500" />
          <span className="font-medium truncate">{contact.email}</span>
        </a>
      </div>

      {/* CTA big button */}
      {cta && (
        <div className="px-5 pb-4">
          <Link
            href={cta.href}
            className="inline-flex items-center justify-center w-full h-12 rounded-xl bg-accent-500 hover:bg-accent-600 text-white text-sm font-semibold uppercase tracking-wider transition-colors shadow-sm"
          >
            {pickLocalized(cta.label, locale)}
          </Link>
        </div>
      )}

      {/* Settings row: locale | theme | social — kompakt, tek satir */}
      <div className="flex items-center justify-between px-5 py-3 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-100 dark:border-slate-800">
        <LocaleControl {...(enabledLocales ? { enabledLocales } : {})} />
        <span className="h-4 w-px bg-slate-200 dark:bg-slate-700" aria-hidden="true" />
        <ThemeControl />
        {social.length > 0 && (
          <>
            <span className="h-4 w-px bg-slate-200 dark:bg-slate-700" aria-hidden="true" />
            <div className="flex items-center gap-1" aria-label={t('social_links')}>
              {social.map((s) => (
                <a
                  key={s.platform}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={pickLocalized(s.label, locale)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 dark:text-slate-400 hover:text-brand-700 dark:hover:text-brand-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  {s.platform === 'linkedin' && <Linkedin size={15} aria-hidden="true" />}
                </a>
              ))}
            </div>
          </>
        )}
      </div>
    </footer>
  )
}

// ─── Inline controls (footer satiri icin) ──────────────────────────────

/**
 * LocaleControl — mobile menü footer'ında dil değiştirici.
 *
 * `enabledLocales` prop (parent'tan gelir) ile runtime filtre — disabled diller
 * gizlenir. 3+ dilde grid wrap olur (touch-friendly).
 *
 * Build-time SUPPORTED_LOCALES 15 dili içerir (routing için); burada sadece
 * admin'in aktif ettiği diller gösterilir.
 */
function LocaleControl({ enabledLocales }: { enabledLocales?: readonly string[] }) {
  const currentLocale = useLocale() as Locale
  const t = useTranslations('a11y')
  const router = useRouter()
  const pathname = usePathname()
  const [isPending, startTransition] = useTransition()

  const visibleLocales = useMemo<readonly Locale[]>(() => {
    if (!enabledLocales || enabledLocales.length === 0) return SUPPORTED_LOCALES
    return SUPPORTED_LOCALES.filter((l) => enabledLocales.includes(l))
  }, [enabledLocales])

  function switchTo(target: Locale) {
    if (target === currentLocale || isPending) return

    // DOM alternates check — locale switcher pattern (hreflang linkleri kullan)
    if (typeof window !== 'undefined') {
      const alt = document.querySelector<HTMLLinkElement>(
        `link[rel="alternate"][hreflang="${target}"]`,
      )
      if (alt?.href) {
        startTransition(() => {
          window.location.href = alt.href
        })
        return
      }
    }
    startTransition(() => {
      router.replace(pathname, { locale: target })
    })
  }

  return (
    <div
      className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs font-semibold tracking-wider"
      role="group"
      aria-label={t('locale_switcher')}
    >
      {visibleLocales.map((loc, idx) => {
        const isActive = loc === currentLocale
        return (
          <span key={loc} className="inline-flex items-center">
            <button
              type="button"
              onClick={() => switchTo(loc)}
              disabled={isActive || isPending}
              className={
                isActive
                  ? 'text-brand-700 dark:text-brand-300 cursor-default'
                  : 'text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 transition-colors cursor-pointer'
              }
              {...(isActive && { 'aria-current': 'true' as const })}
              aria-label={LOCALE_LABELS[loc] ?? loc.toUpperCase()}
            >
              {loc.toUpperCase()}
            </button>
            {idx < visibleLocales.length - 1 && (
              <span className="ms-1.5 text-slate-300 dark:text-slate-700" aria-hidden="true">
                /
              </span>
            )}
          </span>
        )
      })}
    </div>
  )
}

/**
 * ThemeControl — minimal sun/moon icon toggle, footer satiri icin.
 */
function ThemeControl() {
  const t = useTranslations('theme')
  const { resolvedTheme, setTheme } = useTheme()
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  const isDark = mounted && resolvedTheme === 'dark'

  return (
    <button
      type="button"
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      aria-label={t('toggle_label')}
      className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
    >
      {mounted ? (
        isDark ? (
          <Sun size={15} aria-hidden="true" />
        ) : (
          <Moon size={15} aria-hidden="true" />
        )
      ) : (
        <Sun size={15} aria-hidden="true" />
      )}
    </button>
  )
}
